import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, Download, Trash2, Search, Filter, 
  ChevronLeft, ChevronRight, Eye, CheckCircle, 
  Clock, XCircle, UserCheck, ArrowLeft 
} from 'lucide-react';
import api from '../utils/api';
import { getUser } from '../utils/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const statusColors = {
  new: 'bg-blue-100 text-blue-700',
  reviewed: 'bg-yellow-100 text-yellow-700',
  contacted: 'bg-purple-100 text-purple-700',
  hired: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700'
};

const statusIcons = {
  new: Clock,
  reviewed: Eye,
  contacted: UserCheck,
  hired: CheckCircle,
  rejected: XCircle
};

export default function AdminResumeSubmissions() {
  const navigate = useNavigate();
  const user = getUser();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSector, setFilterSector] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const itemsPerPage = 10;

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/login');
      return;
    }
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const response = await api.get('/admin/resume-submissions');
      setSubmissions(response.data);
    } catch (error) {
      toast.error('Failed to fetch submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadResume = async (submissionId, filename) => {
    try {
      const response = await api.get(`/admin/resume-submissions/${submissionId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `resume_${submissionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Resume downloaded');
    } catch (error) {
      toast.error('Failed to download resume');
    }
  };

  const handleUpdateStatus = async (submissionId, newStatus) => {
    try {
      await api.put(`/admin/resume-submissions/${submissionId}/status?status=${newStatus}`);
      setSubmissions(submissions.map(s => 
        s.id === submissionId ? { ...s, status: newStatus } : s
      ));
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    
    try {
      await api.delete(`/admin/resume-submissions/${submissionId}`);
      setSubmissions(submissions.filter(s => s.id !== submissionId));
      toast.success('Submission deleted');
    } catch (error) {
      toast.error('Failed to delete submission');
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/admin/resume-submissions/export', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `resume_submissions_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export submissions');
    }
  };

  // Filter and search
  const filteredSubmissions = submissions.filter(s => {
    const matchesSearch = !searchTerm || 
      `${s.first_name} ${s.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.phone?.includes(searchTerm);
    
    const matchesSector = !filterSector || s.job_sector === filterSector;
    const matchesStatus = !filterStatus || s.status === filterStatus;
    
    return matchesSearch && matchesSector && matchesStatus;
  });

  // Get unique sectors for filter
  const sectors = [...new Set(submissions.map(s => s.job_sector).filter(Boolean))];

  // Pagination
  const totalPages = Math.ceil(filteredSubmissions.length / itemsPerPage);
  const paginatedSubmissions = filteredSubmissions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen py-32 px-6 flex items-center justify-center">
        <div className="text-lg text-zinc-500">Loading submissions...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-32 px-6 md:px-12 bg-zinc-50" data-testid="admin-resume-submissions">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <button 
              onClick={() => navigate('/dashboard/admin')}
              className="text-sm text-blue-600 hover:text-blue-700 mb-2 inline-flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Dashboard
            </button>
            <h1 className="text-3xl font-bold text-zinc-900">Resume Submissions</h1>
            <p className="text-zinc-500 mt-1">{submissions.length} total submissions</p>
          </div>
          <Button 
            onClick={handleExport}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="export-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export to Excel
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 mb-6 border border-zinc-200 flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </div>
          <select
            value={filterSector}
            onChange={(e) => setFilterSector(e.target.value)}
            className="px-4 py-2 border border-zinc-200 rounded-lg text-sm"
            data-testid="sector-filter"
          >
            <option value="">All Sectors</option>
            {sectors.map(sector => (
              <option key={sector} value={sector}>{sector}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-zinc-200 rounded-lg text-sm"
            data-testid="status-filter"
          >
            <option value="">All Status</option>
            <option value="new">New</option>
            <option value="reviewed">Reviewed</option>
            <option value="contacted">Contacted</option>
            <option value="hired">Hired</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-500 uppercase">Candidate</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-500 uppercase">Contact</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-500 uppercase">Job Sector</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-500 uppercase">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-500 uppercase">Submitted</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-zinc-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {paginatedSubmissions.map((submission) => {
                  const StatusIcon = statusIcons[submission.status] || Clock;
                  return (
                    <tr key={submission.id} className="hover:bg-zinc-50" data-testid={`submission-row-${submission.id}`}>
                      <td className="px-6 py-4">
                        <div className="font-medium text-zinc-900">
                          {submission.first_name} {submission.last_name}
                        </div>
                        {submission.resume_filename && (
                          <div className="text-sm text-zinc-500 flex items-center mt-1">
                            <FileText className="w-3 h-3 mr-1" />
                            {submission.resume_filename}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-zinc-900">{submission.email}</div>
                        <div className="text-sm text-zinc-500">{submission.phone || 'No phone'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                          {submission.job_sector || 'General'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={submission.status || 'new'}
                          onChange={(e) => handleUpdateStatus(submission.id, e.target.value)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border-0 cursor-pointer ${statusColors[submission.status] || statusColors.new}`}
                          data-testid={`status-select-${submission.id}`}
                        >
                          <option value="new">New</option>
                          <option value="reviewed">Reviewed</option>
                          <option value="contacted">Contacted</option>
                          <option value="hired">Hired</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 text-sm text-zinc-500">
                        {formatDate(submission.submitted_at)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {submission.resume_filename && (
                            <button
                              onClick={() => handleDownloadResume(submission.id, submission.resume_filename)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Download Resume"
                              data-testid={`download-btn-${submission.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(submission.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Submission"
                            data-testid={`delete-btn-${submission.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredSubmissions.length === 0 && (
            <div className="text-center py-12 text-zinc-500">
              No submissions found
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-zinc-200 flex items-center justify-between">
              <div className="text-sm text-zinc-500">
                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredSubmissions.length)} of {filteredSubmissions.length}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => p - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-zinc-600">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
